<script>
  import dateformat from 'dateformat';
  import lodash from 'lodash';
  import FuelUtilities from './FuelUtilities';

  export default {
    components: {},
    mixins: [FuelUtilities],
    props: {
      searchString: String,
      startDate: [String, Date],
      endDate: [String, Date],
      filterBusinessId: String,
      filterUserId: Number,
      viewAsUserId: Number,
      user: Object,
    },
    data() {
      return {
        baseUrl: '',
        title: '',
        viewAllTitle: '',
        isInitialized: false,
        itemsPerPage: 10,
        items: [],
        defaultItemsPerPage: 10,
        defaultSortDir: 'asc',
        defaultSortProp: 'title',
        currentPageItems: [],
        filteredItems: [],
        isLoading: false,
        searchFields: ['title'],
        sortDir: null,
        sortProp: null,
        page: 1,
      };
    },
    mounted() {
      this.initialize();
    },

    computed: {
      sortClass() {
        return this.sortDir === 'asc' ?
          'glyphicon-arrow-up' : 'glyphicon-arrow-down';
      },
      viewingAll() {
        return this.title === this.viewAllTitle;
      },
      visible() {
        return this.viewAllTitle === '' || this.viewingAll;
      },
      filterLastAction() {
        return [this.endDate, this.startDate];
      },
      noItems() {
        return this.loading === false && this.filteredItems.length === 0;
      },
    },
    methods: {
      initialize() {
        // this has to be set to the opposite so that the first time sort is called it reverses correctly
        this.sortDir = this.defaultSortDir === 'asc' ? 'desc' : 'asc';
      },
      reset() {
        this.filteredItems = this.items;
        this.reinitializeSortDir();
        this.sortBy(this.isInitialized ? this.sortProp : this.defaultSortProp);
        this.paginate();
      },
      viewAll() {
        this.itemsPerPage = this.items.length;
        this.paginate();
        this.$emit('view-all', { title: this.title });
      },
      reinitializeSortDir() {
        // this has to be set to the opposite so that when reset calls sort, it maintains the correct order
        this.sortDir = (this.sortDir === 'asc' ? 'desc' : 'asc');
      },
      sortBy(property) {
        // if we were previously sorting by this property
        if (this.sortProp === property) {
          // set new sort direction to the opposite of what it was
          this.sortDir = (this.sortDir === 'asc') ? 'desc' : 'asc';
          // if we haven't changed our sort property, just reverse the array
          this.filteredItems.reverse();
        } else {
          // otherwise, always start ascending
          this.sortDir = this.defaultSortDir;

          // update sort property
          this.sortProp = property;

          // sort filteredItems by property, this will always be in asc order
          this.filteredItems = lodash.sortBy(this.filteredItems, [
            item => this.prepareValueForComparison(item[property])
          ]);
        }
        this.paginate(this.page);
      },
      prepareValueForComparison(value) {
        // if we have a string, we need to do some special operations
        if (typeof value === 'string' || value instanceof String) {
          // if we are sorting by a date, transform to instance of moment
          if (value.match(/[0-9]{4}-[0-9]{2}-[0-9]{2}/)) {
            return new Date(value);
          }
          // if we are working with currency, make it a number
          if (value.match(/^\$/)) {
            return Number(value.replace(/^\$/, '').match(/[0-9.]/g).join(''));
          }
          // if vanilla string, convert to lowercase to make sort case insensitive
          return value.toLowerCase();
        }
        return value;
      },
      paginationHint() {
        const itemsStart = ((this.page - 1) * this.itemsPerPage) + 1;
        const itemsFinish = (itemsStart - 1) + this.currentPageItems.length;
        return `${itemsStart}-${itemsFinish}/${this.filteredItems.length}`;
      },
      pageChanged(props) {
        this.paginate(props.page);
      },
      paginate(page = 1) {
        this.setPage(page);
        this.getPageItems(page);
      },
      setPage(page = 1) {
        this.page = page;
      },
      getPageItems(page = 1) {
        const offset = this.itemsPerPage * (page > 0 ? page - 1 : 0);
        this.currentPageItems = this.filteredItems
          .slice(offset, (offset + this.itemsPerPage));
      },
      initializeSearch() {
        this.reset();
        this.filterByString();
      },
      filterByLastAction() {
        if (this.startDate || this.endDate) {
          if (this.startDate !== '') {
            this.filteredItems = lodash.filter(this.filteredItems, (item) => {
              const startDate = new Date(this.startDate);
              const theStartDate = dateformat(startDate, 'yyyymmdd');
              const itemLastAction = new Date(item.lastAction);
              const theItemLastAction = dateformat(itemLastAction, 'yyyymmdd');
              return theStartDate <= theItemLastAction;
            });
          }
          if (this.endDate !== '') {
            this.filteredItems = lodash.filter(this.filteredItems, (item) => {
              const endDate = new Date(this.endDate);
              const theEndDate = dateformat(endDate, 'yyyymmdd');
              const itemLastAction = new Date(item.lastAction);
              const theItemLastAction = dateformat(itemLastAction, 'yyyymmdd');
              return theItemLastAction <= theEndDate;
            });
          }
          this.paginate();
        }
      },
      filterByString() {
        if (this.searchString != '') {
          this.filteredItems = lodash.filter(
            this.filteredItems,
            item => this.matchesSearch(item),
          );
          this.paginate();
        }
      },
      matchesSearch(item) {
        // for each field that we'll match, perform search filter
        return lodash.chain(this.searchFields)
          .map(field => this.performSearchFilter(item[field]))
          // remove false values
          .compact()
          .value()
          // if we had at a positive return true
          .length > 0;
      },
      performSearchFilter(value) {
        return (
          value && value.toLowerCase().indexOf(this.searchString.toLowerCase()) >= 0
        );
      },
    },
  }
</script>
